// Device-fingerprint helper for the admin-owner login flow.
//
// Purpose:
//   The gao-id-worker `/v2/admin/owner/auth/verify` endpoint accepts
//   an optional `deviceFingerprintHash` field — a 64-char lowercase
//   hex SHA-256 of an opaque per-browser fingerprint that the FE
//   stores locally. When the env flag `ADMIN_REQUIRE_TRUSTED_DEVICE`
//   is "true", the BE refuses to mint a session without it; when
//   unset, the BE mints a device-unbound session that the BFF gate
//   (`enforceAdminControlAccess` with hardcoded
//   `requireTrustedDevice: true`) subsequently rejects with
//   `device_required` → 401. Either way, the admin-web canonical
//   login flow requires the FE to send the field.
//
// Fingerprint material:
//   A random 32-byte value generated once per browser and persisted
//   in `localStorage` under `gao.admin.device.fp`. This is NOT a true
//   per-device hash (no UA/screen/timezone heuristics) — it is a
//   stable browser-scoped opaque token. The threat model here is the
//   same as a cookie: the operator approves the device once via
//   `POST /v2/admin/owner/devices/:id/trust`, and from then on the
//   browser that holds the localStorage key sees mint-success on
//   every login. Clearing localStorage produces a fresh fingerprint
//   and a fresh approval requirement. Storage in `localStorage` (not
//   `sessionStorage`) is intentional: the fingerprint must outlive
//   tab close so re-login does not re-trigger the approval ceremony.
//
// Hashing:
//   `SHA-256(raw)` via `crypto.subtle.digest`. The hash is what the
//   BE persists and compares against; the raw value never leaves the
//   browser. The BE treats the hash as an opaque 64-char hex token
//   per the runbook (`docs/runbooks/admin-owner-access-model.md`).
//
// What this helper does NOT do:
//   * No third-party fingerprint library. No UA / canvas / WebGL
//     fingerprinting. The fingerprint is a stable random token, not a
//     cross-site tracker.
//   * No raw fingerprint material is ever sent to the network. Only
//     the SHA-256 hash leaves the browser.
//   * No private-key derivation. No identity binding beyond the
//     browser-scoped localStorage row.

const STORAGE_KEY = "gao.admin.device.fp";

/**
 * Convert raw bytes to a lowercase hex string. The output matches
 * the 64-char hex shape the BE regex `/^[0-9a-f]{64}$/i` accepts.
 */
function bytesToHex(bytes: Uint8Array): string {
  let out = "";
  for (let i = 0; i < bytes.length; i++) {
    out += bytes[i]!.toString(16).padStart(2, "0");
  }
  return out;
}

/**
 * Lazily creates (and persists) a per-browser fingerprint and
 * returns its SHA-256 hash as a 64-char lowercase hex string. Safe
 * to call repeatedly; same browser yields the same hash until
 * localStorage is cleared.
 *
 * Returns `null` only when both `localStorage` and `crypto.subtle`
 * are unavailable (e.g. SSR, very old browsers). Callers should
 * treat `null` as "skip the field" — the BE either falls back to
 * device-unbound (Impl-B) or refuses with `device_required`
 * (Impl-C), both of which the existing Login phase machine handles.
 */
export async function getOrCreateDeviceFingerprintHash(): Promise<
  string | null
> {
  if (
    typeof window === "undefined" ||
    typeof window.crypto?.subtle?.digest !== "function" ||
    typeof window.localStorage === "undefined"
  ) {
    return null;
  }
  let raw = window.localStorage.getItem(STORAGE_KEY);
  if (raw === null || raw.length === 0) {
    const buf = new Uint8Array(32);
    window.crypto.getRandomValues(buf);
    raw = bytesToHex(buf);
    window.localStorage.setItem(STORAGE_KEY, raw);
  }
  const encoded = new TextEncoder().encode(raw);
  const digestBuf = await window.crypto.subtle.digest("SHA-256", encoded);
  return bytesToHex(new Uint8Array(digestBuf));
}

/**
 * Exposed for tests + operator forensics. Returns the raw stored
 * fingerprint material (NOT the hash) if present. Tests use this to
 * derive expected hash; operators do NOT need to read this — clear
 * the localStorage row instead and let the next login generate a
 * fresh one.
 */
export function _readRawDeviceFingerprintForTesting(): string | null {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
    return null;
  }
  return window.localStorage.getItem(STORAGE_KEY);
}

/**
 * Exposed for tests. Clears the stored fingerprint so the next call
 * to `getOrCreateDeviceFingerprintHash` generates a fresh one.
 * Operators should NOT need to call this in normal flow.
 */
export function _clearDeviceFingerprintForTesting(): void {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
    return;
  }
  window.localStorage.removeItem(STORAGE_KEY);
}
